import { calculateSkipIds, shouldSkipObservationQuestion } from './questionnaire-evaluation.utils'

import type { IQuestionnaireQuestion, QuestionAnswerInput, SubjectMember } from '@healthblocks-io/core'
import type { IQuestionnaireWithHealthParameters } from 'index'
import type { QuestionnaireCareplanActivityWithIncludes } from '../types/questionnaire.types'

// ===============================
// Helper functions
// ===============================

/**
 * Determine the engine mode based on the activity
 * @param activity - The activity to determine the engine mode for
 * @returns
 */
export function determineEngineMode(
  activity: QuestionnaireCareplanActivityWithIncludes,
): 'new' | 'edit' | 'reconfirm' | 'readOnly' {
  let mode: 'new' | 'edit' | 'readOnly' | 'reconfirm' = 'new'

  if (activity.questionnaireResponse) {
    switch (activity.questionnaireResponse.status) {
      case 'completed': {
        mode = 'readOnly'
        break
      }
      case 'in-progress': {
        mode = 'edit'
        break
      }
      case 'amended': {
        mode = 'reconfirm'
        break
      }
    }
  }

  return mode
}

/**
 * Get the answerable questions and prune the answers that are not valid or answerable anymore
 * @param questionnaire - The questionnaire to get the answerable questions for
 * @param answers - The answers to get the answerable questions for
 * @param subject - The subject to get the answerable questions for
 * @returns The answerable questions and the pruned answers
 */
export function getAnswerableQuestionsAndPrune(
  questionnaire: IQuestionnaireWithHealthParameters,
  answers: { [linkId: string]: QuestionAnswerInput | undefined },
  subject: SubjectMember,
): {
  answerableQuestions: IQuestionnaireQuestion[]
  answers: { [linkId: string]: QuestionAnswerInput }
} {
  // Build evaluation-only answers: auto-default missing Observation review
  // answers to 'edit' so child question conditions can resolve.
  // Explicit user answers ('keep' or 'edit') are never overridden.
  const evaluationAnswers: {
    [linkId: string]: QuestionAnswerInput | undefined
  } = { ...answers }

  for (const q of questionnaire.item) {
    if (q.type !== 'Observation') continue

    const linkId = q.linkId || q.id || ''
    if (!linkId) continue

    if (shouldSkipObservationQuestion(q)) {
      const previous = evaluationAnswers[linkId]

      if (previous?.value !== 'edit' && previous?.value !== 'keep') {
        evaluationAnswers[linkId] = {
          ...(previous ?? { linkId }),
          linkId,
          value: 'edit',
          text: 'edit',
        }
      }
    }
  }

  const skipIds = calculateSkipIds(questionnaire, evaluationAnswers, subject)

  const answerableQuestions = questionnaire.item.filter((question) => {
    const idOrLinkId = question.linkId || question.id || ''
    return !skipIds.includes(idOrLinkId)
  })

  // Prune answers from the ORIGINAL answers (not the evaluation copy).
  // Visible questions are always preserved. Non-visible answers survive only
  // when they belong to a preserved category (observation reviews, health
  // parameter children linked to a 'keep' observation).
  const visibleLinkIds = new Set(answerableQuestions.map((q) => q.linkId || q.id || ''))

  const preservedHiddenLinkIds = buildPreservedHiddenLinkIds(questionnaire, answers)

  const prunedAnswers: { [linkId: string]: QuestionAnswerInput } = {}
  for (const [key, ans] of Object.entries(answers)) {
    if (ans && (visibleLinkIds.has(key) || preservedHiddenLinkIds.has(key))) {
      prunedAnswers[key] = ans
    }
  }

  return { answerableQuestions, answers: prunedAnswers }
}

/**
 * Determines which non-visible answers should survive pruning.
 *
 * Two categories:
 * 1. Observation review answers with an explicit value ('keep' or 'edit') —
 *    these represent user decisions that must persist even when the question
 *    itself is auto-skipped (e.g. no healthParameter data on the definition).
 * 2. Health parameter child answers linked to a 'keep' observation —
 *    these are snapshot values that must persist even though the child
 *    questions are hidden by skip logic when the review answer is 'keep'.
 */
export function buildPreservedHiddenLinkIds(
  questionnaire: IQuestionnaireWithHealthParameters,
  answers: { [linkId: string]: QuestionAnswerInput | undefined },
): Set<string> {
  const preserved = new Set<string>()

  const keepSlugs = new Set<string>()

  for (const question of questionnaire.item) {
    if (question.type !== 'Observation') continue

    const linkId = question.linkId || question.id || ''
    if (!linkId) continue

    const answer = answers[linkId]

    if (answer?.value === 'keep' || answer?.value === 'edit') {
      preserved.add(linkId)
    }

    if (answer?.value === 'keep') {
      const questionnaireSlug = question.questionnaireSlug
      if (questionnaireSlug) {
        keepSlugs.add(questionnaireSlug)
      }
    }
  }

  for (const question of questionnaire.item) {
    const linkId = question.linkId || question.id || ''
    if (!linkId) continue

    if (question.for && keepSlugs.has(question.for)) {
      preserved.add(linkId)
    }
  }

  return preserved
}

/**
 * Get the linkId of a questionnaire question
 * @param q - The questionnaire question to get the linkId for
 * @returns The linkId of the questionnaire question
 */
export const getLinkIdOfQuestionnaireQuestion = (q: IQuestionnaireQuestion) => q.linkId || q.id || ''

/**
 * For reconfirm mode deeplinks: If deeplinking to an observation question
 * whose root observation review question is 'keep', automatically change it to 'edit'
 * so the observation questions become visible.
 *
 * This is necessary because when a root observation question is answered 'keep',
 * all observation questions linked to it (via the 'for' property) are filtered out
 * and not included in answerableQuestions. When deeplinking to such a question,
 * we need to change 'keep' to 'edit' so the question becomes answerable.
 *
 * @param questionnaire - The questionnaire
 * @param answers - Current answers map
 * @param initialLinkId - The deeplink linkId
 * @returns Modified answers with 'keep' changed to 'edit' if needed
 */
export function autoChangeKeepToEditForDeeplink(
  questionnaire: IQuestionnaireWithHealthParameters,
  answers: Record<string, QuestionAnswerInput>,
  initialLinkId: string,
): Record<string, QuestionAnswerInput> {
  // Find the question being deeplinked to
  const deeplinkQuestion = questionnaire.item.find((q) => (q.linkId || q.id || '') === initialLinkId)

  // Only process if this question has a 'for' property (it's an observation question)
  if (!deeplinkQuestion?.for) {
    return answers
  }

  // Find the root observation question (type === 'Observation' with matching questionnaireSlug)
  const rootObservationQuestion = questionnaire.item.find(
    (q) => q.type === 'Observation' && q.healthParameter && q.questionnaireSlug === deeplinkQuestion.for,
  )

  if (!rootObservationQuestion) {
    return answers
  }

  const rootLinkId = rootObservationQuestion.linkId || rootObservationQuestion.id || ''
  const rootAnswer = answers[rootLinkId]

  // If root observation question is 'keep', change it to 'edit'
  if (rootAnswer?.value === 'keep') {
    return {
      ...answers,
      [rootLinkId]: {
        ...rootAnswer,
        value: 'edit',
        text: 'edit',
      },
    }
  }

  return answers
}

/**
 * For reconfirm mode deeplinks: If deeplinking to an Observation anchor question,
 * auto-set its answer to 'edit' and return the first dependent's linkId so
 * navigation starts at the dependent (skipping the anchor).
 *
 * The pencil click in the reconfirmation overview already expresses edit intent,
 * so showing the anchor's keep/edit choice is redundant.
 *
 * @returns Modified answers and the dependent linkId to navigate to, or null if
 *          the deeplink target is not an Observation anchor with dependents.
 */
export function resolveObservationAnchorDeeplink(
  questionnaire: IQuestionnaireWithHealthParameters,
  answers: Record<string, QuestionAnswerInput>,
  initialLinkId: string,
): {
  answers: Record<string, QuestionAnswerInput>
  targetLinkId: string
} | null {
  const question = questionnaire.item.find((q) => (q.linkId || q.id || '') === initialLinkId)

  if (!question || question.type !== 'Observation') return null

  const questionnaireSlug = question.questionnaireSlug
  if (!questionnaireSlug) return null

  // Find the first dependent question (linked via 'for' property)
  const firstDependent = questionnaire.item.find((q) => q.for === questionnaireSlug)
  if (!firstDependent) return null

  const firstDependentLinkId = firstDependent.linkId || firstDependent.id || ''
  if (!firstDependentLinkId) return null

  const anchorLinkId = question.linkId || question.id || ''

  return {
    answers: {
      ...answers,
      [anchorLinkId]: {
        linkId: anchorLinkId,
        value: 'edit',
        text: 'edit',
      },
    },
    targetLinkId: firstDependentLinkId,
  }
}
